import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { FaAnglesRight } from "react-icons/fa6";
import { GoTrophy } from "react-icons/go";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { MdOutlineAnalytics, MdQueryStats } from "react-icons/md";

import * as S from "./styles";
import {
  allLeaguesId,
  leaguesBackImage,
  leaguesData,
  leaguesIdName,
  leaguesNames,
} from "./data";
import { NavLink, useNavigate } from "react-router-dom";
import { FutButton } from "@/components/FutButton";
import FutDatePicker from "@/components/FutDatePicker";

import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

// import required modules
import { Pagination } from "swiper/modules";
import { MatchData, OddsData } from "@/interfaces/liveGamesType";
import { api, apiPublic } from "@/services/api";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { catchError } from "@/utils/catchError";
import { StringToNumber } from "@/@types/customTypes";
import { BiFilterAlt } from "react-icons/bi";
import RoundInput from "@/components/RoundInput";
import { FaSearch } from "react-icons/fa";
import useGameData from "@/hooks/useGameData";
import { useAuth } from "@/context/AuthContext";
import Login from "@/components/Login";

interface futFormType {
  isLiveMatch: boolean;
  datePicker: Date;
  searchMatch: string;
}

interface IGameData extends MatchData {
  loading?: boolean;
}
export default function HomeLp() {
  const { isAuthenticated } = useAuth();

  const [isLeaguesOpen, setIsLeaguesOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [liveMatches, setLiveMatches] = useState<MatchData[]>([]);
  const [historyMatches, setHistoryMatches] = useState<MatchData[]>([]);
  const [gamesData, setGamesData] = useState<IGameData[]>([]);
  const [gamesPages, setGamesPages] = useState({
    liveMatch: { page: 1, end: false },
    historyMatch: { page: 1, end: false },
    loading: false,
  });
  const [selectedLeague, setSelectedLeague] = useState(0);
  const [openMobileModal, setOpenMobileModal] = useState(false);
  const [loginModal, setLoginModal] = useState(false);

  const PAGE_SIZE = 20;
  const theme = useTheme();
  const navigate = useNavigate();
  const isMedia1200 = !useMediaQuery(theme.breakpoints.up("lg"));
  const isMedia900 = !useMediaQuery(theme.breakpoints.up("md"));

  const futForm = useForm<futFormType>({
    defaultValues: {
      datePicker: new Date(),
      isLiveMatch: false,
      searchMatch: "",
    },
  });

  const watchSearchMatch = futForm.watch("searchMatch");

  const isLiveMatch = futForm.watch("isLiveMatch");

  useEffect(() => {
    getLiveGames(true);
    getGameHistory();
    const interval = setInterval(() => {
      getLiveGames(false);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const getGameHistory = async () => {
    try {
      setIsLoading(true);
      const historyGames = await apiPublic.get("/football/getHistMatchesFree", {
        params: {
          from: format(futForm.getValues("datePicker"), "yyyy-MM-dd"),
          to: format(new Date(), "yyyy-MM-dd"),
          pageSize: 20,
          page: gamesPages.historyMatch.page,
          ligas: encodeURI(allLeaguesId.toString()),
        },
      });

      const historyData = historyGames.data.body?.data || historyGames.data?.data || [];
      setHistoryMatches(historyData);

      if (!futForm.getValues("isLiveMatch")) {
        setGamesData(historyData);
      }
    } catch (e) {
      toast.error(
        "Ocorreu um erro ao procurar os jogos históricos, tente novamente mais tarde"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getLiveGames = async (needLoading: boolean) => {
    try {
      if (needLoading && futForm.getValues("isLiveMatch")) {
        setIsLoading(true);
      }
      const games = await apiPublic.get("/football/getLiveMatchesFree", {
        params: { ligas: encodeURI(allLeaguesId.toString()) },
      });

      const allLiveMatches = games.data.body?.data || games.data?.data || [];

      if (games.data.body?.error || games.data?.error) {
        return;
      }

      setLiveMatches(allLiveMatches);

      if (futForm.getValues("isLiveMatch")) {
        setGamesData(allLiveMatches);
      }
    } catch (e) {
      toast.error(
        "Ocorreu um erro ao procurar os jogos ao vivo, tente novamente mais tarde"
      );
    } finally {
      if (needLoading && futForm.getValues("isLiveMatch")) {
        setIsLoading(false);
      }
    }
  };

  const filterGames = async (leagueId: number) => {
    const liveMatch = futForm.getValues("isLiveMatch");
    const storedGameData = [...gamesData];

    if (leagueId === 0) {
      if (liveMatch) {
        getLiveGames(true);
        return;
      }
      getGameHistory();
      return;
    }

    setSelectedLeague(leagueId);

    if (liveMatch) {
      const filtered = storedGameData.filter(
        (el) => el.league_id === leagueId.toString()
      );

      setGamesData(filtered);
      return;
    }

    try {
      setIsLoading(true);
      const getPicker = format(futForm.getValues("datePicker"), "yyyy-MM-dd");
      const filtered = await apiPublic.get("/football/getHistMatchesFree", {
        params: {
          ligas: leagueId,
          from: getPicker,
          to: getPicker,
          page: 1,
          pageSize: PAGE_SIZE,
        },
      });

      // Verificar a estrutura da resposta e extrair os dados corretamente
      const filteredData = filtered.data.body?.data || filtered.data?.data || [];
      const filteredLength = filteredData.length;

      if (filteredLength < 20) {
        setGamesPages((prev) => ({
          ...prev,
          historyMatch: { ...prev.historyMatch, end: true },
        }));
      } else {
        setGamesPages((prev) => ({
          ...prev,
          historyMatch: { page: 1, end: false },
        }));
      }

      setHistoryMatches(filteredData);
      setGamesData(filteredData);
    } catch (e) {
      toast.error(
        "Ocorreu um erro ao filtrar os jogos, tente novamente mais tarde"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const liveMatch = event.target.checked;

    futForm.setValue("isLiveMatch", liveMatch);

    filterGames(selectedLeague);
  };

  const stateController = (str: "leagues") => {
    if (str === "leagues") {
      setIsLeaguesOpen((prev) => !prev);
    }
  };

  const findGamesMatches = () => {
    const query = futForm.getValues("searchMatch");

    if (!query) {
      if (futForm.getValues("isLiveMatch")) {
        setGamesData(liveMatches);
        setOpenMobileModal(false);
        return;
      }
      setGamesData(historyMatches);
      setOpenMobileModal(false);
      return;
    }

    const filteredGames = gamesData.filter(
      (obj) =>
        obj.match_awayteam_name.toLowerCase().includes(query.toLowerCase()) ||
        obj.match_hometeam_name.toLowerCase().includes(query.toLowerCase()) ||
        obj.country_name.toLowerCase().includes(query.toLowerCase()) ||
        obj.match_time.toLowerCase().includes(query.toLowerCase())
    );

    setGamesData(filteredGames);
    setOpenMobileModal(false);
  };

  const getCorrectLeagueName = (leagueName: string) => {
    try {
      const getPartialName = leagueName.slice(0, 6);
      const nameIndex = leaguesNames.findIndex((el) =>
        el.includes(getPartialName)
      );

      if (nameIndex !== -1) {
        return leaguesNames[nameIndex];
      }

      return "";
    } catch (e) {
      return "";
    }
  };

  const handleModal = () => {
    if (isAuthenticated) {
      navigate("/dashboard");
      return;
    }
    setLoginModal(true);
  };

  const ShowMoreHandler = async () => {
    setIsLoading(true);
    try {
      const historyGames = await apiPublic.get("/football/getHistMatchesFree", {
        params: {
          from: format(futForm.getValues("datePicker"), "yyyy-MM-dd"),
          to: format(new Date(), "yyyy-MM-dd"),
          pageSize: 20,
          page: gamesPages.historyMatch.page + 1,
          ligas: encodeURI(
            selectedLeague !== 0
              ? selectedLeague.toString()
              : allLeaguesId.toString()
          ),
        },
      });

      const historyData = historyGames.data.body?.data || historyGames.data?.data || [];
      const historyLength = historyData.length;

      if (historyLength && !(historyLength < 20)) {
        setGamesPages((prev) => ({
          ...prev,
          historyMatch: { page: prev.historyMatch.page + 1, end: false },
        }));
        setHistoryMatches((prev) => [...prev, ...historyData]);
        setGamesData((prev) => [...prev, ...historyData]);
      } else {
        setGamesPages((prev) => ({
          ...prev,
          historyMatch: { ...prev.historyMatch, end: true },
        }));
      }
    } catch (e) {
      toast.error("Ocorreu um erro ao tentar carregar mais jogos");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <S.HomeContainer>
      <S.FakeNavButton onClick={() => stateController("leagues")}>
        <S.MobileTrophyButton>
          <GoTrophy />
        </S.MobileTrophyButton>
      </S.FakeNavButton>
      <Grid container>
        <Grid
          item
          sm={2}
          paddingTop={isMedia1200 ? 0 : 3}
          display="flex"
          alignItems="flex-start"
        >
          <S.LeagueContainer isOpen={isLeaguesOpen}>
            <h2>
              Ligas <span onClick={() => stateController("leagues")}>X</span>
            </h2>
            {leaguesData.map((el) => (
              <a
                className={el.leagueId === selectedLeague ? "active" : ""}
                onClick={() => {
                  filterGames(el.leagueId);
                }}
                key={el.text}
              >
                <div>
                  <img src={el.img} />
                  <span>{el.text}</span>
                </div>
              </a>
            ))}
          </S.LeagueContainer>
        </Grid>
        <Grid
          item
          sm={isMedia1200 ? 12 : 10}
          padding={isMedia1200 ? 0 : 3}
          maxWidth="100%"
        >
          {/* <Grid container>
            {isMedia1200 ? (
              <Swiper pagination modules={[Pagination]}>
                <SwiperSlide>
                  <S.GptAnalysis>
                    <S.GptCover />
                    <h2>Análise GPT a sua IA para fazer apostas.</h2>
                    <p>
                      Use o nosso sistema de análise GPT para descobrir em tempo
                      real quais são as melhores apostas a se fazer.
                    </p>
                    <FutButton
                      sx={{ mt: 3, width: "150px" }}
                      onClick={handleModal}
                    >
                      Análise GPT
                    </FutButton>
                  </S.GptAnalysis>
                </SwiperSlide>
                <SwiperSlide>
                  <S.ReferFriendsContainer>
                    <h2>Indique amigos e ganhe dinheiro!</h2>
                    <p>
                      Participe do sistema de indicações para se tornar um
                      verdadeiro campeão.
                    </p>
                    <FutButton
                      sx={{ mt: 3 }}
                      onClick={handleModal}
                      variant="outlined"
                    >
                      &nbsp;Começar agora&nbsp; <FaAnglesRight />
                      &nbsp;
                    </FutButton>
                  </S.ReferFriendsContainer>
                </SwiperSlide>
              </Swiper>
            ) : (
              <>
                <Grid item sm={6}>
                  <S.GptAnalysis>
                    <S.GptCover />
                    <h2>Análise GPT a sua IA para fazer apostas.</h2>
                    <p>
                      Use o nosso sistema de análise GPT para descobrir em tempo
                      real quais são as melhores apostas a se fazer.
                    </p>
                    <FutButton
                      sx={{ mt: 3, width: "150px" }}
                      onClick={handleModal}
                    >
                      Análise GPT
                    </FutButton>
                  </S.GptAnalysis>
                </Grid>
                <Grid item sm={6}>
                  <S.ReferFriendsContainer>
                    <h2>Indique amigos e ganhe dinheiro!</h2>
                    <p>
                      Participe do sistema de indicações para se tornar um
                      verdadeiro campeão.
                    </p>
                    <FutButton
                      sx={{ mt: 3 }}
                      onClick={handleModal}
                      variant="outlined"
                    >
                      &nbsp;Começar agora&nbsp; <FaAnglesRight />
                      &nbsp;
                    </FutButton>
                  </S.ReferFriendsContainer>
                </Grid>
              </>
            )}
          </Grid> */}

          <S.GameFilterContainer>
            <S.SwitchButton>
              <input
                {...futForm.register("isLiveMatch")}
                type="checkbox"
                onChange={(event) => {
                  handleCheckbox(event);
                }}
              />
              <S.KnobButton rightName="Ao vivo">
                <div />
                <h3>Jogos passados</h3>
              </S.KnobButton>
              <S.LayerSwitch />
            </S.SwitchButton>

            <S.FilterContainer>
              <S.FilterMobileBtn
                onClick={() => setOpenMobileModal((prev) => !prev)}
              >
                <BiFilterAlt />
              </S.FilterMobileBtn>
            </S.FilterContainer>

            <Modal
              open={openMobileModal}
              onClose={() => setOpenMobileModal(false)}
            >
              <S.ModalMobileContainer>
                <S.ModalMobileContent>
                  <h2>
                    Filtros
                    <span onClick={() => setOpenMobileModal(false)}>X</span>
                  </h2>
                  <p>Data do Jogos Passados</p>
                  <FutDatePicker
                    control={futForm.control}
                    disableFuture
                    minDate={new Date("2000-01-02")}
                    maxDate={new Date()}
                    inputName="datePicker"
                    onAccept={() => {
                      getGameHistory();
                    }}
                    disabled={isLiveMatch}
                  />

                  <p>Pesquisar Por Time</p>

                  <RoundInput
                    inputName="searchMatch"
                    control={futForm.control}
                    endAdornment={
                      <InputAdornment position="end">
                        {watchSearchMatch ? (
                          <IconButton
                            onClick={() => {
                              futForm.setValue("searchMatch", "");
                            }}
                            edge="end"
                          >
                            <IoIosCloseCircleOutline />
                          </IconButton>
                        ) : (
                          <FaSearch />
                        )}
                      </InputAdornment>
                    }
                    fullWidth
                    size="small"
                    autoComplete="email"
                  />

                  <FutButton sx={{ px: 3, mt: 2 }} onClick={findGamesMatches}>
                    Aplicar Filtro
                  </FutButton>
                </S.ModalMobileContent>
              </S.ModalMobileContainer>
            </Modal>
          </S.GameFilterContainer>

          <S.GameBodyContainer>
            <h2>Jogos</h2>
            {isLoading ? (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            ) : (
              <Grid container spacing={2} justifyContent="center">
                {gamesData.length ? (
                  gamesData.map((el, index) => {
                    return (
                      <Grid
                        item
                        xl={4}
                        width="100%"
                        key={`${el.match_awayteam_id} ${el.match_hometeam_id} ${el.match_id}`}
                      >
                        <S.GameCardContainer>
                          <S.GameBackImage>
                            <img
                              src={
                                leaguesBackImage[
                                getCorrectLeagueName(
                                  el.league_name
                                ) as keyof typeof leaguesBackImage
                                ]
                              }
                            />
                          </S.GameBackImage>
                          {el.match_status === "Finished" ? (
                            <S.GameEndContainer>
                              <span>Jogo Finalizado</span>
                            </S.GameEndContainer>
                          ) : null}
                          <S.GameInfoLive>
                            <S.TeamContainer>
                              <img src={el.team_home_badge} />
                              <p>{el.match_hometeam_name}</p>
                              <small>Em Casa</small>
                            </S.TeamContainer>
                            <S.GameScore>
                              <h3>
                                &nbsp; &nbsp; {el.match_hometeam_score} :{" "}
                                {el.match_awayteam_score} &nbsp; &nbsp;
                              </h3>
                              <small>{el.match_time}</small>
                            </S.GameScore>
                            <S.TeamContainer>
                              <img src={el.team_away_badge} />
                              <p>{el.match_awayteam_name}</p>
                              <small>Fora</small>
                            </S.TeamContainer>
                          </S.GameInfoLive>
                          <S.StatisticsButton
                            variant="outlined"
                            onClick={handleModal}
                          >
                            <S.StatisticsContainer>
                              <MdOutlineAnalytics />
                              Acompanhar Estatísticas
                            </S.StatisticsContainer>
                          </S.StatisticsButton>
                          <S.StatisticsButton
                            variant="outlined"
                            onClick={handleModal}
                          >
                            <MdQueryStats />
                            Estatísticas mais detalhadas
                          </S.StatisticsButton>
                        </S.GameCardContainer>
                      </Grid>
                    );
                  })
                ) : (
                  <S.NotFoundContainer>
                    <span>:(</span>
                    <h3>
                      Nenhuma Partida Encontrada Em{" "}
                      {
                        leaguesIdName[
                        selectedLeague as keyof typeof leaguesIdName
                        ]
                      }
                    </h3>
                  </S.NotFoundContainer>
                )}
              </Grid>
            )}
            <Grid item sm={12} display="flex" justifyContent="center">
              {gamesPages.loading ? (
                <CircularProgress />
              ) : futForm.getValues("isLiveMatch") ? null : gamesPages
                .historyMatch.end || !gamesData.length ? null : (
                <S.StatisticsButton
                  variant="outlined"
                  onClick={ShowMoreHandler}
                >
                  Mostrar Mais Jogos
                </S.StatisticsButton>
              )}
            </Grid>
          </S.GameBodyContainer>
        </Grid>
      </Grid>

      <Modal open={loginModal} onClose={() => setLoginModal(false)}>
        <S.WrapperModal>
          <S.ModalContainer>
            <S.WarnContainer>
              <h2>Se logue para ver o conteúdo:</h2>
              <button onClick={() => setLoginModal(false)}>X</button>
            </S.WarnContainer>
            <Login />
          </S.ModalContainer>
        </S.WrapperModal>
      </Modal>
    </S.HomeContainer>
  );
}
